"use client"

import { useEffect, useId, useMemo, useState, type ReactNode } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { CalculatorQuizCta } from "@/components/tools/calculator-quiz-cta"
import { buttonVariants } from "@/components/ui/button"
import { smaf5CalculatorKeys as k } from "@/i18n/smaf5-calculator-keys"
import { formatDecimal } from "@/lib/locale-number-format"
import { track } from "@/lib/analytics"
import { cn } from "@/lib/utils"
import {
  HIGH_FROM,
  LOW_BELOW,
  MAX_AST,
  MAX_HEIGHT_CM,
  MAX_PLATELETS,
  MAX_WEIGHT_KG,
  MIN_AST,
  MIN_HEIGHT_CM,
  MIN_PLATELETS,
  MIN_WEIGHT_KG,
  SEX_OPTIONS,
  SMAF5_BANDS,
  bmiFrom,
  roundBmi,
  smaf5BandFor,
  smaf5IsBmiFloored,
  smaf5Problem,
  smaf5Score,
  roundScore,
  type Smaf5Band,
  type Smaf5Problem,
  type Smaf5Sex,
} from "@/lib/smaf5"

/**
 * The interactive part of /tools/smaf5-calculator.
 *
 * One centred column: the page intro, then the form, then the result panel,
 * which does not exist until Calculate has run. One component owns all three
 * because the form and the result share state; the `intro` slot is what keeps
 * the h1 in the client file, where the page's structure is meant to stay
 * readable.
 *
 * Deliberately framer-motion-free: the widget is always mounted, so any
 * animation here would have to clear the `<AnimatePresence>` + cacheComponents
 * trap (see `/animation-cache-trap`). CSS transitions cover what it needs.
 *
 * Nothing is submitted, stored or tracked. That matters here for the reason it
 * does on the FIB-4 page and then some: between them these six fields are a
 * lab report, a body and a diagnosis, and the arithmetic happens in the
 * browser and never leaves it.
 */

/**
 * The score axis drawn under the result: -4 to +4.
 *
 * Both published cut-offs (0 and 1) sit inside it, at 50% and 62.5%, with room
 * on either side to see how far past one a result lands. The axis has to carry
 * negatives because the score is a logit rather than a probability: a healthy
 * reader typically scores around -2, and clipping the scale at zero would put
 * every low-risk result on the left edge with nothing to compare it against.
 * Outside the range the marker clamps.
 */
const SCALE_MIN = -4
const SCALE_MAX = 4

/** Exported for the reference table's band chips in the client file. */
export const BAND_STYLES: Record<
  Smaf5Band,
  { bar: string; chip: string; accent: string }
> = {
  low: {
    bar: "bg-deep-green-400",
    chip: "bg-deep-green-100 text-deep-green-800",
    accent: "text-deep-green-600",
  },
  intermediate: {
    bar: "bg-terracotta-300",
    chip: "bg-terracotta-100 text-terracotta-900",
    accent: "text-terracotta-600",
  },
  high: {
    bar: "bg-terracotta-600",
    chip: "bg-terracotta-200 text-terracotta-950",
    accent: "text-terracotta-800",
  },
}

const inputClasses =
  "type-body-lg w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-neutral-900 tabular-nums focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-aubergine-400 focus-visible:outline-none"

const unitSuffixClasses =
  "type-body-md pointer-events-none absolute inset-y-0 right-4 flex items-center text-neutral-500"

const labelClasses = "type-label-md block text-neutral-700 normal-case"

const hintClasses = "type-body-sm mt-2 text-neutral-500"

const choiceClasses =
  "type-title-sm flex-1 cursor-pointer rounded-full px-4 py-2.5 transition-colors"

/**
 * `as const`, and at module scope, for the reason `SEX_OPTIONS` in
 * `lib/smaf5.ts` documents: annotating `labelKey` as `string` widens it out of
 * the catalog's key union and next-intl's typed `t()` rejects it.
 */
const DIABETES_OPTIONS = [
  { value: false, labelKey: k.calcDiabetesNo },
  { value: true, labelKey: k.calcDiabetesYes },
] as const

/**
 * NaN for a blank or unparseable field. `Number("")` is 0, which would read a
 * still-empty box as a real zero: an empty height field would submit as 0 cm,
 * and dividing by its square returns Infinity rather than anything the
 * plausibility check would catch as a typo.
 */
function parseField(value: string): number {
  const trimmed = value.trim().replace(",", ".")
  if (trimmed === "") return NaN
  return Number(trimmed)
}

interface Smaf5Result {
  /** Rounded to the two decimals the reader sees, see `roundScore`. */
  score: number
  band: Smaf5Band
  /** The reader's real BMI, not the floored one the model scored. */
  bmi: number
  /** Whether that BMI was raised to the floor. See `BMI_FLOOR`. */
  bmiFloored: boolean
}

export function Smaf5CalculatorWidget({ intro }: { intro?: ReactNode }) {
  const t = useTranslations("smaf5Calculator")
  // The score and the BMI are rendered here rather than coming from the
  // catalog, so the decimal separator has to be chosen explicitly: a French
  // page printing "0.92" beside prose that says "0,92" contradicts itself.
  const locale = useLocale()
  const fieldId = useId()
  /**
   * Flipped in an effect, and stamped onto the root as `data-hydrated`.
   *
   * Not decoration: the inputs are controlled, so anything typed into them
   * before React owns them is silently discarded, and an e2e test has no other
   * way to know when that moment has passed. On the BMI page a blind retry
   * loop kept flaking on a loaded CI runner until this signal existed. One
   * boolean, renders nothing.
   */
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  /**
   * Sex and diabetes start UNSET, rather than defaulting the way the BMR
   * page's sex toggle does.
   *
   * Diabetes is the reason. It adds a flat 2.339 to the score, which is more
   * than the width of the entire intermediate band, so a preselected "No"
   * would quietly hand a reader with type 2 diabetes a result up to two bands
   * too low, on a page whose whole purpose is to tell them whether to get
   * their liver looked at. Sex follows the same rule for consistency, and
   * because there is no defensible default for it either.
   *
   * The cost is that the submit button stays disabled a moment longer, which
   * is the right trade: an unanswered question is visible, a wrong default is
   * not.
   */
  const [sex, setSex] = useState<Smaf5Sex | null>(null)
  const [diabetes, setDiabetes] = useState<boolean | null>(null)
  const [heightCm, setHeightCm] = useState("")
  const [weightKg, setWeightKg] = useState("")
  const [ast, setAst] = useState("")
  const [platelets, setPlatelets] = useState("")

  /**
   * The result only exists after Calculate, and any change to any field drops
   * it. A stale figure sitting beside edited inputs is the one thing a
   * side-by-side layout must not show, and it matters more here than on a BMI
   * page: this number is the one a reader might take to an appointment.
   */
  const [result, setResult] = useState<Smaf5Result | null>(null)
  const [problem, setProblem] = useState<Smaf5Problem | null>(null)

  /** Every field setter goes through this. */
  function invalidate() {
    setResult(null)
    setProblem(null)
  }

  const hasInput =
    sex !== null ||
    diabetes !== null ||
    heightCm.trim() !== "" ||
    weightKg.trim() !== "" ||
    ast.trim() !== "" ||
    platelets.trim() !== ""

  /** The six inputs, or null while any one of them is unanswered. */
  const measurements = useMemo(() => {
    if (sex === null || diabetes === null) return null
    const cm = parseField(heightCm)
    const kg = parseField(weightKg)
    const astValue = parseField(ast)
    const plateletValue = parseField(platelets)
    if (
      !Number.isFinite(cm) ||
      !Number.isFinite(kg) ||
      !Number.isFinite(astValue) ||
      !Number.isFinite(plateletValue)
    ) {
      return null
    }
    return {
      sex,
      diabetes,
      heightCm: cm,
      weightKg: kg,
      ast: astValue,
      platelets: plateletValue,
    }
  }, [sex, diabetes, heightCm, weightKg, ast, platelets])

  /**
   * Plausibility is checked here rather than by disabling the button: a
   * disabled control with no explanation leaves the reader guessing, so the
   * button enables as soon as every field is answered and an unscoreable entry
   * gets the message that explains which of the three things went wrong.
   *
   * The score is rounded first and banded from that same rounded value (see
   * `roundScore`) so the label always describes the number on screen.
   */
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!measurements) return
    const found = smaf5Problem(measurements)
    if (found !== null) {
      setResult(null)
      setProblem(found)
      track.calculatorResult({
        calculator: "smaf5",
        outcome: "rejected",
        reason: found,
      })
      return
    }
    const bmi = bmiFrom(measurements.heightCm, measurements.weightKg)
    const score = roundScore(smaf5Score({ ...measurements, bmi }))
    setProblem(null)
    track.calculatorResult({ calculator: "smaf5", outcome: "result" })
    setResult({
      score,
      band: smaf5BandFor(score),
      bmi: roundBmi(bmi),
      bmiFloored: smaf5IsBmiFloored(bmi),
    })
  }

  function reset() {
    setSex(null)
    setDiabetes(null)
    setHeightCm("")
    setWeightKg("")
    setAst("")
    setPlatelets("")
    invalidate()
  }

  const styles = result ? BAND_STYLES[result.band] : null

  /**
   * Segment widths on the -4 to +4 axis, in score points. Fixed, unlike the
   * FIB-4 page's: there is one set of cut-offs for every adult, because the
   * score carries no age term.
   */
  const segments = [
    // no-raw-ids: band lookup key, never rendered
    { id: "low" as const, span: LOW_BELOW - SCALE_MIN },
    // no-raw-ids: band lookup key, never rendered
    { id: "intermediate" as const, span: HIGH_FROM - LOW_BELOW },
    // no-raw-ids: band lookup key, never rendered
    { id: "high" as const, span: SCALE_MAX - HIGH_FROM },
  ]

  const positionOf = (value: number) =>
    Math.min(
      100,
      Math.max(0, ((value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100)
    )

  const problemMessage = problem
    ? problem === "plateletsUnit"
      ? t(k.calcPlateletsUnit)
      : problem === "bodyOutOfRange"
        ? t(k.calcBodyOutOfRange)
        : t(k.calcOutOfRange)
    : null

  return (
    <div
      data-testid="smaf5-calculator"
      data-hydrated={hydrated ? "true" : undefined}
      /* One centred column at the prose measure, lining up with the
         sections below it. The result no longer needs a column of its
         own: it does not exist until Calculate has run. */
      className="mx-auto w-full max-w-[760px]"
    >
      {/* ── Intro ── */}
      <div className="text-center">{intro}</div>

      {/* ── Form ── */}
      {/*
        `noValidate` is load-bearing, not a style choice.

        The number inputs below carry `min`/`max` so a phone shows a sensible
        keypad and a stepper cannot walk outside the accepted range. But those
        attributes also arm the browser's own constraint validation, which
        runs BEFORE `onSubmit` and cancels the submit outright on a violation.
        On the SCORE2 page that made a field look like a dead button:
        `handleSubmit` never ran, `problem` was never set, and the result
        panel sat on its empty-state line. A CI Playwright run caught it.

        It also has to be `noValidate` rather than dropping `min`/`max`,
        because the browser's bubble cannot say what these messages say.
        "Value must be less than or equal to 1000" is the wrong thing to tell
        someone who entered their platelet count per microlitre: the number is
        not too large, it is in the wrong unit, and that is the one mistake on
        this page that would otherwise print a reassuring low risk over a
        score of roughly minus 1700.
      */}
      <form onSubmit={handleSubmit} noValidate className="mt-10">
        <h2 className="type-title-lg text-center text-neutral-900">
          {t(k.calcTitle)}
        </h2>

        <div className="mt-6 space-y-4">
          {/* Sex — a real radiogroup, styled as a segmented control. */}
          <div role="radiogroup" aria-labelledby={`${fieldId}-sex-label`}>
            <span id={`${fieldId}-sex-label`} className={labelClasses}>
              {t(k.calcSexLabel)}
            </span>
            <div className="mt-2 flex gap-2">
              {SEX_OPTIONS.map((option) => {
                const selected = sex === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`smaf5-sex-${option.value}`}
                    onClick={() => {
                      setSex(option.value)
                      invalidate()
                    }}
                    className={cn(
                      choiceClasses,
                      selected
                        ? "bg-aubergine-800 text-white"
                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    )}
                  >
                    {t(option.labelKey)}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor={`${fieldId}-height`} className={labelClasses}>
                {t(k.calcHeightLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-height`}
                  data-testid="smaf5-height"
                  type="number"
                  inputMode="numeric"
                  min={MIN_HEIGHT_CM}
                  max={MAX_HEIGHT_CM}
                  step="1"
                  autoComplete="off"
                  placeholder="165"
                  value={heightCm}
                  onChange={(e) => {
                    setHeightCm(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-14")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitCm)}</span>
              </div>
            </div>
            <div>
              <label htmlFor={`${fieldId}-weight`} className={labelClasses}>
                {t(k.calcWeightLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-weight`}
                  data-testid="smaf5-weight"
                  type="number"
                  inputMode="decimal"
                  min={MIN_WEIGHT_KG}
                  max={MAX_WEIGHT_KG}
                  step="0.1"
                  autoComplete="off"
                  placeholder="70"
                  value={weightKg}
                  onChange={(e) => {
                    setWeightKg(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-14")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitKg)}</span>
              </div>
            </div>
          </div>
          <p className={hintClasses}>{t(k.calcBodyHint)}</p>

          {/* Diabetes — the heaviest term in the score, and unset until
              answered. See the state declaration above. */}
          <div role="radiogroup" aria-labelledby={`${fieldId}-diabetes-label`}>
            <span id={`${fieldId}-diabetes-label`} className={labelClasses}>
              {t(k.calcDiabetesLabel)}
            </span>
            <div className="mt-2 flex gap-2">
              {DIABETES_OPTIONS.map((option) => {
                const selected = diabetes === option.value
                return (
                  <button
                    key={String(option.value)}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`smaf5-diabetes-${option.value ? "yes" : "no"}`}
                    onClick={() => {
                      setDiabetes(option.value)
                      invalidate()
                    }}
                    className={cn(
                      choiceClasses,
                      selected
                        ? "bg-aubergine-800 text-white"
                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    )}
                  >
                    {t(option.labelKey)}
                  </button>
                )
              })}
            </div>
            <p className={hintClasses}>{t(k.calcDiabetesHint)}</p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor={`${fieldId}-ast`} className={labelClasses}>
                {t(k.calcAstLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-ast`}
                  data-testid="smaf5-ast"
                  type="number"
                  inputMode="decimal"
                  min={MIN_AST}
                  max={MAX_AST}
                  step="1"
                  autoComplete="off"
                  placeholder="30"
                  value={ast}
                  onChange={(e) => {
                    setAst(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-16")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitUl)}</span>
              </div>
            </div>
            <div>
              <label htmlFor={`${fieldId}-platelets`} className={labelClasses}>
                {t(k.calcPlateletsLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-platelets`}
                  data-testid="smaf5-platelets"
                  type="number"
                  inputMode="decimal"
                  min={MIN_PLATELETS}
                  max={MAX_PLATELETS}
                  step="1"
                  autoComplete="off"
                  placeholder="250"
                  value={platelets}
                  onChange={(e) => {
                    setPlatelets(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-16")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitGl)}</span>
              </div>
            </div>
          </div>
          <p className={hintClasses}>{t(k.calcBloodHint)}</p>
          <p className={hintClasses}>{t(k.calcPlateletsHint)}</p>
        </div>

        <div className="mt-6 flex items-center gap-4">
          {/*
            `buttonVariants` on a bare <button> rather than <Button>: the
            idiom the other calculators use, and it keeps `data-testid` off a
            custom component's prop type.
          */}
          <button
            type="submit"
            data-testid="smaf5-submit"
            disabled={measurements === null}
            className={cn(buttonVariants({ variant: "primary" }), "flex-1")}
          >
            {t(k.calcSubmit)}
          </button>
          {hasInput && (
            <button
              type="button"
              onClick={reset}
              className="type-title-sm shrink-0 cursor-pointer text-neutral-500 underline underline-offset-4 transition-colors hover:text-neutral-900"
            >
              {t(k.calcReset)}
            </button>
          )}
        </div>

        {/* Who the score applies to, next to the button rather than buried
            further down the page: a reader in the middle of an acute
            hepatitis needs to know before they read a number, not after. */}
        <p className={hintClasses}>{t(k.calcEligibilityNote)}</p>

        {/*
          The refusal message used to be the result panel's status line. With
          that panel gone until Calculate succeeds, it belongs beside the
          control that produced it. `data-state` still names WHICH refusal is
          showing, so a test can tell them apart without asserting copy in
          three languages — and there is no longer an "empty" value for a
          green-looking spec to pass on by accident.
        */}
        {problemMessage !== null && (
          <p
            data-testid="smaf5-status"
            data-state={problem}
            className="type-body-md text-terracotta-700 mt-4 text-center"
          >
            {problemMessage}
          </p>
        )}
      </form>

      {/* ── Result ── */}
      {/*
        The live region is mounted from the start so the panel is
        announced when it arrives; the panel itself only exists once
        Calculate has produced a figure, which is the point of the
        layout.
      */}
      <div aria-live="polite">
        {result !== null && styles !== null && (
          <div
            data-testid="smaf5-result"
            className="mt-8 rounded-[16px] bg-white p-6 md:p-8"
          >
            <div>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="type-label-md text-neutral-500 normal-case">
                    {t(k.calcResultLabel)}
                  </p>
                  {/* No unit after the number: sMAF-5 is an index on a log-odds
                      scale, and two decimals is how the authors print it. */}
                  <p
                    data-testid="smaf5-value"
                    className={cn(
                      "type-display-md mt-1 tabular-nums",
                      styles.accent
                    )}
                  >
                    {formatDecimal(result.score, 2, locale)}
                  </p>
                </div>
                {/*
                  `data-band` carries the band ID beside the translated label.
                  The three labels share no substring across en/de/fr ("Low
                  risk", "Tiefes Risiko", "Risque bas"), so a text regex cannot
                  check the band in every locale the page serves, and asserting
                  the English wording would red the whole suite on a translation
                  pass.
                */}
                <span
                  data-testid="smaf5-band"
                  data-band={result.band}
                  className={cn(
                    "type-title-sm rounded-full px-4 py-1.5",
                    styles.chip
                  )}
                >
                  {t(SMAF5_BANDS[result.band].labelKey)}
                </span>
              </div>

              {/* The BMI the reader's height and weight produced, shown because
                  it is the one input the form does not ask for directly and the
                  one the floor note below refers to. This is their real BMI, not
                  the floored value the model scored: printing 25 for someone who
                  entered 165 cm and 58 kg would look like an arithmetic bug. */}
              <p
                data-testid="smaf5-bmi"
                className="type-body-sm mt-3 text-neutral-500"
              >
                {t(k.calcBmiLabel)} {formatDecimal(result.bmi, 1, locale)}
              </p>

              {/* Scale */}
              <div className="mt-6">
                <p className="type-label-md text-neutral-500 normal-case">
                  {t(k.calcThresholdsLabel)}
                </p>
                <div className="relative mt-3">
                  {/*
                    Contiguous, no gap between segments. A gap would push every
                    boundary right of its true position while the marker is
                    placed on the ungapped score, so the marker would stop landing
                    on the band it names. Rounding lives on the clipping container
                    instead of the individual segments.
                  */}
                  <div className="flex h-2 w-full overflow-hidden rounded-full">
                    {segments.map((segment) => (
                      <div
                        key={segment.id}
                        className={cn("h-full", BAND_STYLES[segment.id].bar)}
                        style={{
                          width: `${(segment.span / (SCALE_MAX - SCALE_MIN)) * 100}%`,
                        }}
                      />
                    ))}
                  </div>
                  <div
                    aria-hidden
                    className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-neutral-900 bg-white transition-[left] duration-300 ease-out"
                    style={{ left: `${positionOf(result.score)}%` }}
                  />
                </div>
                {/*
                  Ticks are absolutely positioned at their real place on the axis.
                  The two cut-offs are not evenly spaced on it (0 sits at 50% of
                  the -4 to +4 axis and 1 at 62.5%), so a flex justify-between row
                  would print them at even intervals and mislabel the bands
                  underneath.
                */}
                <div aria-hidden className="relative mt-3 h-4">
                  <span className="type-label-sm absolute top-0 left-0 text-neutral-400 normal-case tabular-nums">
                    {formatDecimal(SCALE_MIN, 2, locale)}
                  </span>
                  {[LOW_BELOW, HIGH_FROM].map((value) => (
                    <span
                      key={value}
                      className="type-label-sm absolute top-0 -translate-x-1/2 text-neutral-400 normal-case tabular-nums"
                      style={{ left: `${positionOf(value)}%` }}
                    >
                      {formatDecimal(value, 2, locale)}
                    </span>
                  ))}
                  <span className="type-label-sm absolute top-0 right-0 text-neutral-400 normal-case tabular-nums">
                    {formatDecimal(SCALE_MAX, 2, locale)}+
                  </span>
                </div>
                <p className="type-body-sm mt-2 text-neutral-500">
                  {t(k.calcScaleCaption)}
                </p>
              </div>

              <p
                data-testid="smaf5-meaning"
                className="type-body-md mt-6 border-t border-neutral-200 pt-5 text-neutral-600"
              >
                {t(SMAF5_BANDS[result.band].meaningKey)}
              </p>

              {/* Shown only below a BMI of 25, where the model's BMI term stops
                  moving. A caveat and not a refusal: the score IS the published
                  score at a BMI of 21, and what that reader needs to know is
                  that it was computed as though they were at 25, so a low result
                  proves less than it looks. */}
              {result.bmiFloored && (
                <p
                  data-testid="smaf5-bmi-floor-note"
                  className="type-body-sm mt-4 text-neutral-500"
                >
                  {t(k.calcBmiFloorNote)}
                </p>
              )}
            </div>
            {/*
              Quiz hand-off. Inside the result panel and gated on `result`, so
              it is the reward for pressing Calculate rather than another CTA
              sitting on the page from the start.
              It hands the reader to the quiz (CalculatorQuizCta), and it does NOT
              claim the quiz reads this result: the quiz asks about goals and
              history, and saying otherwise would be a promise it cannot keep.
            */}
            <div className="bg-aubergine-50 mt-6 rounded-lg p-5">
              <h3 className="type-title-md text-neutral-900">
                {t(k.quizHeading)}
              </h3>
              <p className="type-body-md mt-2 text-neutral-600">
                {t(k.quizBody)}
              </p>
              <CalculatorQuizCta calculator="smaf5">
                {t(k.quizCta)}
              </CalculatorQuizCta>
            </div>
          </div>
        )}
      </div>

      {/* The caveat is about the tool, not the answer, so it stays on
          the page whether or not anything has been calculated. */}
      <p className="type-body-sm mx-auto mt-8 max-w-[600px] text-center text-neutral-500">
        {t(k.calcDisclaimer)}
      </p>
    </div>
  )
}
